import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
    Plus, Search, Pencil, Ban, RotateCcw, Pill, Package, MapPin, Snowflake, Lock,
    AlertTriangle, PackageX, Store as StoreIcon,
} from 'lucide-react';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import Pagination from '../../components/Pagination';
import CommonLoader from '../../components/CommonLoader';
import AddMedicine from '../../components/modals/AddMedicine';
// Imported under a non-`use` name on purpose — the default export of
// hooks/useAxiosAuth.js is a plain factory, and binding it as `useAxiosAuth`
// makes react-hooks/rules-of-hooks flag every call site. See usePermissions.js.
import createAuthedAxios from '../../hooks/useAxiosAuth';
import usePermissions from '../../hooks/usePermissions';

const MySwal = withReactContent(Swal);

// THE PHARMACY'S OWN LIST — the medicines and products this hospital sells,
// priced and located. Built by the pharmacy itself (owner, 10 Oct 2026): there
// is no Super Admin master to pick from and no starter catalogue, so a new
// pharmacy starts empty. Each row is a MEDICINE or a PRODUCT, the same choice
// as the doctor's own list (a product has no form and no schedule).
//
// It is deliberately NOT the same thing as stock. The catalogue is tenant-wide
// (one Amoxicillin 500 record, however many counters dispense it) while every
// quantity on this screen belongs to ONE counter, which is why there is a store
// picker at the top that changes the numbers and nothing else. A screen that
// silently summed both counters would tell an OP pharmacist they hold twelve
// strips when the twelve are on a ward two floors up.
//
// WHY THE BIN CODE IS A BADGE AND NOT A COLUMN OF GREY TEXT
// The slowest step at a pharmacy counter is not billing, it is walking the room
// looking for the strip. The home bin is the answer to "where do I go", so it is
// given the visual weight of an answer. Cold-chain and narcotic bins are
// coloured differently for the same reason they are on RackLocations: a strip
// picked from the wrong kind of storage is a compliance failure, not a tidiness
// one.
//
// NO REDUX SLICE. Every other module here has one, but a slice has to be
// registered in src/store.js, and this screen ships before that wiring exists.
// The calls below are plain axios through the same authed factory the slices
// use, so moving them into a pharmacySlice later is a copy, not a rewrite.

// ---------------------------------------------------------------------------

const apiError = (err, fallback) =>
    err?.response?.data?.message || err?.message || fallback;

// The server caps `limit` at 200 (pharmacyMasterController.paging), so anything
// that genuinely needs the whole set has to walk the pages. The cap below stops
// a hospital with an enormous batch table from firing fifty requests at page
// load — past it we say the figures are partial rather than quietly show a
// number that is too low, because an understated stock figure is what makes
// somebody order what they already have.
const SWEEP_LIMIT = 200;
const MAX_SWEEP_PAGES = 10;

async function sweep(axiosInstance, url, params = {}) {
    const rows = [];
    let page = 1;
    let totalPages = 1;
    do {
        const res = await axiosInstance.get(url, { params: { ...params, page, limit: SWEEP_LIMIT } });
        rows.push(...(res?.data?.data || []));
        totalPages = res?.data?.totalPages || 1;
        page += 1;
    } while (page <= totalPages && page <= MAX_SWEEP_PAGES);
    return { rows, truncated: totalPages > MAX_SWEEP_PAGES };
}

// The counter's day is India's. A browser sitting in another timezone must not
// decide an Indian pharmacy's expiry cut-off differently from the server, which
// pins the same boundary in SQL (`NOW() AT TIME ZONE 'Asia/Kolkata'`). Getting
// this wrong reads as a rounding error and means a batch that expired yesterday
// is counted as sellable for the first five and a half hours of every working
// day.
const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;
const todayIst = () => new Date(Date.now() + IST_OFFSET_MS).toISOString().slice(0, 10);

// Mirrors LOCATION_TYPES in Backend/api/controllers/pharmacyMasterController.js.
// Only the colouring lives here; RackLocations.jsx carries the full labels and
// the compliance notes, because that is the screen where somebody decides what
// kind of bin they are creating. Kept as two small shaped maps rather than one
// shared module until a third screen needs it.
const BIN_TONE = {
    cold_chain: 'bg-sky-50 text-sky-800 border-sky-300',
    narcotic_cupboard: 'bg-rose-50 text-rose-800 border-rose-400',
    otc_front: 'bg-[#D7A05C]/10 text-[#8a6127] border-[#D7A05C]/50',
    bulk: 'bg-slate-100 text-slate-700 border-slate-300',
    shelf: 'bg-[#4ABDBD]/10 text-[#2D8A8A] border-[#4ABDBD]/40',
};

// Anything other than 'none' cannot leave the counter without a prescription
// reference — the dispense endpoint refuses it outright. Amber for H (a
// prescription), red for H1 and X (a prescription AND a bound register entry),
// so the two are never mistaken for each other at a glance.
const SCHEDULE_TONE = {
    H: 'bg-amber-100 text-amber-800 border-amber-300',
    H1: 'bg-red-100 text-red-700 border-red-400',
    X: 'bg-red-600 text-white border-red-700',
};

const FORMS = ['tablet', 'capsule', 'syrup', 'injection', 'ointment', 'drops', 'other'];
const SCHEDULES = ['none', 'H', 'H1', 'X'];
const TYPES = ['medicine', 'product'];

// The exact set Backend/api/routes/pharmacyMasterRoutes.js gates GET /batches
// on. Batches carry purchaseRate — what the hospital paid — so the stock sweep
// is only attempted by somebody the server would answer. Asking first turns a
// screenful of 403s into one column that politely says nothing.
const STOCK_READ_PERMISSIONS = ['pharmacy.stock.manage', 'pharmacy.purchase.manage', 'pharmacy.return'];

const fmtQty = (n) => (Number.isFinite(Number(n)) ? String(Math.round(Number(n) * 100) / 100) : '0');

export default function MedicineMaster() {
    const { t } = useTranslation();
    const { can, canAny } = usePermissions();
    // One memoised instance: it reads the access token inside its request
    // interceptor, so a single copy stays correct across a token refresh.
    const axiosInstance = useMemo(() => createAuthedAxios(), []);

    const canManage = can('pharmacy.stock.manage');
    const canReadStock = canAny(STOCK_READ_PERMISSIONS);

    const [stores, setStores] = useState([]);
    const [storeId, setStoreId] = useState('');
    const [locations, setLocations] = useState([]);

    const [medicines, setMedicines] = useState([]);
    const [total, setTotal] = useState(0);
    const [listLoading, setListLoading] = useState(true);

    // medicineId -> { onHand, sellable }. Absent means "we were not allowed to
    // look", which is a different thing from zero and is rendered differently.
    const [stockBy, setStockBy] = useState(null);
    const [stockTruncated, setStockTruncated] = useState(false);
    // The reorder list is readable by anyone with a pharmacy permission, so the
    // flag survives even when the exact balances do not.
    const [lowBy, setLowBy] = useState({});

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [activeTab, setActiveTab] = useState('active');
    const [typeFilter, setTypeFilter] = useState('');
    const [formFilter, setFormFilter] = useState('');
    const [scheduleFilter, setScheduleFilter] = useState('');

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [refreshKey, setRefreshKey] = useState(0);
    const [mutating, setMutating] = useState(false);

    const searchTimeoutRef = useRef(null);

    const refetch = useCallback(() => setRefreshKey((k) => k + 1), []);

    useEffect(() => {
        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = setTimeout(() => {
            setDebouncedSearch(searchQuery.trim());
            setCurrentPage(1);
        }, 500);
        return () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); };
    }, [searchQuery]);

    // The counters, once. The default one is first in the response, and a
    // hospital running a single counter should never have to choose it.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await axiosInstance.get('/pharmacy/stores', { params: { status: 'active' } });
                if (cancelled) return;
                const rows = res?.data?.data || [];
                setStores(rows);
                setStoreId((current) => current || rows.find((s) => s.isDefault)?.storeId || rows[0]?.storeId || '');
            } catch (err) {
                if (!cancelled) toast.warn(apiError(err, t('medicinepage.stores_error', 'Could not load the pharmacy counters')));
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // The bin catalogue. Needed twice over: to turn each medicine's locationId
    // into the badge on its row, and to fill the home-bin picker in the modal.
    //
    // Retired bins are FETCHED, not filtered out. The server refuses to retire a
    // bin anything still calls home, so a medicine pointing at one should be
    // impossible — but if it ever happens, the row has to show the real code
    // rather than "no bin", because "no bin" sends somebody looking for a filing
    // mistake that is already recorded. The modal filters them out of the picker
    // instead, which is where offering one would actually do harm.
    useEffect(() => {
        if (!storeId) return undefined;
        let cancelled = false;
        (async () => {
            try {
                const { rows } = await sweep(axiosInstance, '/pharmacy/locations', { storeId });
                if (!cancelled) setLocations(rows);
            } catch (err) {
                if (!cancelled) toast.warn(apiError(err, t('medicinepage.bins_error', 'Could not load the rack bins')));
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storeId, refreshKey]);

    const binById = useMemo(
        () => Object.fromEntries((locations || []).map((l) => [l.locationId, l])),
        [locations]
    );

    // The catalogue page itself. Tenant-wide and therefore independent of the
    // store picker — only the quantities below react to it.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            setListLoading(true);
            try {
                const res = await axiosInstance.get('/pharmacy/medicines', {
                    params: {
                        page: currentPage,
                        limit: itemsPerPage,
                        search: debouncedSearch || undefined,
                        type: typeFilter || undefined,
                        form: formFilter || undefined,
                        schedule: scheduleFilter || undefined,
                        isActive: activeTab === 'active' ? 'true' : 'false',
                    },
                });
                if (cancelled) return;
                setMedicines(res?.data?.data || []);
                setTotal(res?.data?.total || 0);
            } catch (err) {
                if (cancelled) return;
                setMedicines([]);
                setTotal(0);
                toast.warn(apiError(err, t('medicinepage.list_error', 'Failed to load the drug master')));
            } finally {
                if (!cancelled) setListLoading(false);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, itemsPerPage, debouncedSearch, activeTab, typeFilter, formFilter, scheduleFilter, refreshKey]);

    // WHAT IS ACTUALLY ON THE SHELF, assembled once per counter rather than
    // per row. No endpoint returns a per-medicine balance — /medicines is the
    // catalogue and /batches is the stock — so the balances are summed here from
    // one sweep of the batch table and reused across every page of the list.
    // Refetching it per page (or per row) would be dozens of requests for a
    // number that does not change while somebody pages through a table.
    useEffect(() => {
        if (!storeId) return undefined;
        let cancelled = false;
        (async () => {
            // The reorder list first: everyone in the pharmacy may read it, so
            // the flag on the right of each row survives even for a dispenser
            // who is not allowed to see purchase-priced batch rows.
            try {
                const low = await axiosInstance.get('/pharmacy/stock/low', { params: { storeId, limit: SWEEP_LIMIT } });
                if (cancelled) return;
                setLowBy(Object.fromEntries((low?.data?.data || []).map((r) => [r.medicineId, r])));
            } catch {
                if (!cancelled) setLowBy({});
            }

            if (!canReadStock) { if (!cancelled) setStockBy(null); return; }
            try {
                const { rows, truncated } = await sweep(axiosInstance, '/pharmacy/batches', { storeId });
                if (cancelled) return;
                const today = todayIst();
                const acc = {};
                rows.forEach((b) => {
                    const cell = acc[b.medicineId] || (acc[b.medicineId] = { onHand: 0, sellable: 0, undated: 0 });
                    const qty = Number(b.qtyOnHand) || 0;
                    cell.onHand += qty;
                    // The same eligibility the counter applies: an expired,
                    // undated or quarantined batch is physically present and in
                    // the valuation, but it cannot be handed to a patient. Rows
                    // where the two figures differ are the ones worth acting on.
                    // An undated batch is kept apart: for a PRODUCT it IS
                    // sellable (owner, 10 Oct 2026), and the batch row does not
                    // say which the medicine is — StockCell does.
                    if (b.status === 'active' && b.expiryDate && String(b.expiryDate).slice(0, 10) >= today) {
                        cell.sellable += qty;
                    } else if (b.status === 'active' && !b.expiryDate) {
                        cell.undated += qty;
                    }
                });
                setStockBy(acc);
                setStockTruncated(truncated);
            } catch {
                // A 403 here is a permission we mis-guessed, not an outage.
                // Falling back to "unknown" keeps the catalogue usable.
                if (!cancelled) { setStockBy(null); setStockTruncated(false); }
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storeId, refreshKey, canReadStock]);

    const openCreate = () => { setEditing(null); setIsModalOpen(true); };
    const openEdit = (medicine) => { setEditing(medicine); setIsModalOpen(true); };

    const handlePageChange = (page, newItemsPerPage) => {
        if (newItemsPerPage && newItemsPerPage !== itemsPerPage) {
            setItemsPerPage(newItemsPerPage);
            setCurrentPage(1);
        } else {
            setCurrentPage(page);
        }
    };

    /**
     * Discontinue. The server refuses while sellable stock remains, and that
     * refusal is worth showing verbatim: deactivating does NOT stop FEFO
     * dispensing the stock, it only removes the medicine from the counter's
     * search — so the result would be stock that can still leave the shelf but
     * that nobody can find, price or count.
     */
    const handleDiscontinue = async (medicine) => {
        const result = await MySwal.fire({
            title: t('medicinepage.discontinue_title', 'Discontinue this medicine?'),
            text: t('medicinepage.discontinue_text', 'It disappears from the counter search. Nothing is deleted — batches, bills and the ledger keep pointing at it.'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#e5e7eb',
            confirmButtonText: t('medicinepage.discontinue', 'Discontinue'),
            cancelButtonText: t('medicinepage.cancel', 'Cancel'),
        });
        if (!result.isConfirmed) return;

        setMutating(true);
        try {
            await axiosInstance.delete(`/pharmacy/medicines/${medicine.medicineId}`);
            toast.success(t('medicinepage.discontinued', 'Medicine discontinued'));
            refetch();
        } catch (err) {
            toast.warn(apiError(err, t('medicinepage.discontinue_error', 'Failed to discontinue the medicine')));
        } finally {
            setMutating(false);
        }
    };

    const handleReactivate = async (medicine) => {
        setMutating(true);
        try {
            await axiosInstance.put(`/pharmacy/medicines/${medicine.medicineId}`, { isActive: true });
            toast.success(t('medicinepage.reactivated', 'Medicine is back in the catalogue'));
            refetch();
        } catch (err) {
            toast.warn(apiError(err, t('medicinepage.reactivate_error', 'Failed to bring the medicine back')));
        } finally {
            setMutating(false);
        }
    };

    const indexOfFirst = (currentPage - 1) * itemsPerPage;
    const selectedStore = stores.find((s) => s.storeId === storeId) || null;

    // --- row-level renderers -------------------------------------------------

    const ScheduleBadge = ({ schedule }) => {
        if (!schedule || schedule === 'none') {
            return (
                <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-slate-100 text-slate-500 text-[11px] font-semibold border border-slate-200">
                    {t('medicinepage.otc', 'OTC')}
                </span>
            );
        }
        return (
            <span
                title={t('medicinepage.schedule_hint', 'Schedule drug — the counter will not dispense it without a prescription reference')}
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-bold border ${SCHEDULE_TONE[schedule] || SCHEDULE_TONE.H}`}
            >
                {schedule === 'X' && <Lock className="w-3 h-3" />}
                {t('medicinepage.schedule_prefix', 'Sch.')} {schedule}
            </span>
        );
    };

    // Only a product is badged: it is the exception on a pharmacy's list, and
    // the badge is what tells the pharmacist there is no salt, form or
    // schedule to look for. Same amber as the doctor's own list.
    const TypeBadge = () => (
        <span
            data-testid="medicine-type-product"
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-50 text-amber-700 border border-amber-200 text-[11px] font-semibold"
        >
            <Package className="w-3 h-3" />
            {t('medicinesTab.type_product', 'Product')}
        </span>
    );

    const BinBadge = ({ locationId }) => {
        const bin = binById[locationId];
        if (!bin) {
            // A medicine with no home bin is not a cosmetic gap: it is the one
            // the pharmacist hunts for by memory, which is how an expired pack
            // gets handed over.
            return (
                <span
                    data-testid="bin-badge-unmapped"
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-amber-50 text-amber-700 border border-dashed border-amber-300 text-[11px] font-semibold"
                >
                    <AlertTriangle className="w-3 h-3" />
                    {t('medicinepage.no_bin', 'No bin')}
                </span>
            );
        }
        return (
            <span
                title={bin.rackName ? `${bin.rackName} · ${bin.code}` : bin.code}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-sm font-bold border ${BIN_TONE[bin.type] || BIN_TONE.shelf}`}
            >
                {bin.type === 'cold_chain' && <Snowflake className="w-3.5 h-3.5" />}
                {bin.type === 'narcotic_cupboard' && <Lock className="w-3.5 h-3.5" />}
                {!['cold_chain', 'narcotic_cupboard'].includes(bin.type) && <MapPin className="w-3.5 h-3.5" />}
                {bin.code}
            </span>
        );
    };

    const StockCell = ({ medicine }) => {
        const low = lowBy[medicine.medicineId];
        const reorderLevel = Number(medicine.reorderLevel) || 0;

        // ZERO AND UNKNOWN ARE NOT THE SAME ANSWER, and the difference decides
        // whether a buyer orders. `stockBy` being null means we were never
        // allowed to look; a medicine simply missing from a sweep we DID run
        // holds none, so it reads zero rather than falling through to the dash.
        const swept = stockBy ? (stockBy[medicine.medicineId] || { onHand: 0, sellable: 0, undated: 0 }) : null;
        const exact = swept
            ? { onHand: swept.onHand, sellable: swept.sellable + (medicine.type === 'product' ? swept.undated : 0) }
            : null;

        // The reorder list carries a sellable balance for exactly the rows that
        // are short, so it fills the gap for a dispenser who may not read the
        // purchase-priced batch rows at all.
        const onHand = exact ? exact.onHand : (low ? Number(low.qtyOnHand) : null);
        const sellable = exact ? exact.sellable : (low ? Number(low.qtyOnHand) : null);
        const unsellable = exact ? Math.round((exact.onHand - exact.sellable) * 100) / 100 : 0;

        if (onHand === null) {
            return (
                <span className="text-slate-300" title={t('medicinepage.stock_hidden', 'Stock balances need a stock or purchase permission')}>—</span>
            );
        }

        const needsReorder = reorderLevel > 0 && sellable <= reorderLevel;

        return (
            <div>
                <div className="flex items-baseline gap-1">
                    <span className={`text-sm font-bold ${sellable === 0 ? 'text-red-600' : 'text-gray-700'}`}>
                        {fmtQty(onHand)}
                    </span>
                    <span className="text-[11px] text-slate-400">{medicine.unit || 'unit'}</span>
                </div>
                {unsellable > 0 && (
                    <div className="text-[10px] text-amber-600 font-semibold">
                        {t('medicinepage.unsellable', '{{qty}} expired / held', { qty: fmtQty(unsellable) })}
                    </div>
                )}
                {sellable === 0 ? (
                    <span className="inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded bg-red-50 text-red-600 border border-red-200 text-[10px] font-bold">
                        <PackageX className="w-3 h-3" />
                        {t('medicinepage.out_of_stock', 'Out of stock')}
                    </span>
                ) : needsReorder ? (
                    <span className="inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200 text-[10px] font-bold">
                        <AlertTriangle className="w-3 h-3" />
                        {t('medicinepage.reorder', 'Reorder · level {{level}}', { level: fmtQty(reorderLevel) })}
                    </span>
                ) : null}
            </div>
        );
    };

    const Actions = ({ medicine, compact = false }) => (
        <div className={`flex ${compact ? '' : 'justify-center'} gap-2`}>
            <button
                data-testid={`edit-medicine-${medicine.medicineId}`}
                onClick={() => openEdit(medicine)}
                disabled={!canManage}
                title={canManage ? t('medicinepage.edit', 'Edit') : t('medicinepage.no_permission', 'You do not have permission to change the drug master')}
                className={`text-[#4ABDBD] p-2 rounded-xl bg-gradient-to-r from-blue-50 to-cyan-50 shadow-md transition-all duration-300 ${canManage ? 'hover:text-white hover:from-[#4ABDBD] hover:to-[#3AA8A8] cursor-pointer hover:scale-110' : 'opacity-40 cursor-not-allowed'}`}
            >
                <Pencil className="h-4 w-4" />
            </button>
            {medicine.isActive ? (
                <button
                    data-testid={`discontinue-medicine-${medicine.medicineId}`}
                    onClick={() => handleDiscontinue(medicine)}
                    disabled={!canManage || mutating}
                    title={t('medicinepage.discontinue', 'Discontinue')}
                    className={`text-red-500 p-2 rounded-xl bg-gradient-to-r from-red-50 to-pink-50 shadow-md transition-all duration-300 ${canManage && !mutating ? 'hover:text-white hover:from-red-500 hover:to-pink-500 cursor-pointer hover:scale-110' : 'opacity-40 cursor-not-allowed'}`}
                >
                    <Ban className="h-4 w-4" />
                </button>
            ) : (
                <button
                    data-testid={`reactivate-medicine-${medicine.medicineId}`}
                    onClick={() => handleReactivate(medicine)}
                    disabled={!canManage || mutating}
                    title={t('medicinepage.reactivate', 'Bring back')}
                    className={`text-emerald-600 p-2 rounded-xl bg-gradient-to-r from-emerald-50 to-teal-50 shadow-md transition-all duration-300 ${canManage && !mutating ? 'hover:text-white hover:from-emerald-500 hover:to-teal-500 cursor-pointer hover:scale-110' : 'opacity-40 cursor-not-allowed'}`}
                >
                    <RotateCcw className="h-4 w-4" />
                </button>
            )}
        </div>
    );

    return (
        <div data-testid="medicines-page-container" className="bg-white p-4 rounded-2xl shadow-md sm:p-6 w-full">
            <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
                <div className="ml-2">
                    <h1 data-testid="medicines-page-title" className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent">
                        {t('medicinepage.title', 'Medicines & products')}
                    </h1>
                    <p className="text-xs text-slate-400 mt-1">
                        {t('medicinepage.subtitle', 'Everything this pharmacy stocks — what it is, what it is taxed at, and which bin to walk to.')}
                    </p>
                </div>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
                    <button
                        data-testid="add-medicine-button"
                        onClick={openCreate}
                        disabled={listLoading || !canManage}
                        title={canManage ? undefined : t('medicinepage.no_permission', 'You do not have permission to change the drug master')}
                        className={`group flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg transition-all duration-300 font-semibold text-sm whitespace-nowrap ${listLoading || !canManage ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:shadow-xl hover:scale-105'}`}
                    >
                        <Plus className="w-5 h-5" />
                        {t('medicinepage.add_item', 'Add medicine / product')}
                    </button>
                </div>
            </div>

            <div className="mb-4 flex flex-col sm:flex-row gap-2 sm:items-center flex-wrap">
                <div className="relative flex bg-gradient-to-r from-gray-50 to-blue-50/30 p-1 rounded-2xl shadow border border-gray-200 gap-2">
                    {['active', 'inactive'].map((tab) => (
                        <button
                            key={tab}
                            data-testid={`medicines-tab-${tab}`}
                            onClick={() => { setActiveTab(tab); setCurrentPage(1); }}
                            className={`relative px-6 py-2 text-sm rounded-xl transition-colors duration-300 flex-1 z-10 ${activeTab === tab ? 'text-white' : 'text-gray-600 hover:text-[#4ABDBD] cursor-pointer'}`}
                        >
                            {activeTab === tab && (
                                <motion.div
                                    layoutId="activeMedicineStatusTab"
                                    className="absolute inset-0 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] rounded-xl shadow-lg !m-0"
                                    transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                                />
                            )}
                            <span className="relative z-20">
                                {tab === 'active'
                                    ? t('medicinepage.in_catalogue', 'In catalogue')
                                    : t('medicinepage.discontinued_tab', 'Discontinued')}
                            </span>
                        </button>
                    ))}
                </div>

                <div className="relative w-full sm:w-80">
                    <input
                        data-testid="medicines-search-input"
                        type="text"
                        placeholder={t('medicinepage.search_placeholder', 'Search name / salt / brand / barcode…')}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value.replace(/[<>]/g, '').slice(0, 60))}
                        maxLength={60}
                        className="w-full p-2 pl-10 pr-8 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                    />
                    <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#4ABDBD]" />
                </div>

                <select
                    data-testid="medicines-type-filter"
                    value={typeFilter}
                    onChange={(e) => { setTypeFilter(e.target.value); setCurrentPage(1); }}
                    className="tt-select w-full sm:w-40 p-2 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                >
                    <option value="">{t('medicinesTab.filter_all', 'All')}</option>
                    {TYPES.map((tp) => (
                        <option key={tp} value={tp}>
                            {tp === 'product' ? t('medicinesTab.filter_product', 'Products') : t('medicinesTab.filter_medicine', 'Medicines')}
                        </option>
                    ))}
                </select>

                <select
                    data-testid="medicines-form-filter"
                    value={formFilter}
                    onChange={(e) => { setFormFilter(e.target.value); setCurrentPage(1); }}
                    className="tt-select w-full sm:w-40 p-2 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                >
                    <option value="">{t('medicinepage.all_forms', 'All forms')}</option>
                    {FORMS.map((f) => (
                        <option key={f} value={f}>{t(`medicinepage.form_${f}`, f)}</option>
                    ))}
                </select>

                <select
                    data-testid="medicines-schedule-filter"
                    value={scheduleFilter}
                    onChange={(e) => { setScheduleFilter(e.target.value); setCurrentPage(1); }}
                    className="tt-select w-full sm:w-44 p-2 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                >
                    <option value="">{t('medicinepage.all_schedules', 'All schedules')}</option>
                    {SCHEDULES.map((s) => (
                        <option key={s} value={s}>
                            {s === 'none' ? t('medicinepage.otc_long', 'OTC (no schedule)') : `${t('medicinepage.schedule_prefix', 'Sch.')} ${s}`}
                        </option>
                    ))}
                </select>
            </div>

            {/* The catalogue is one hospital's; the quantities are one counter's.
                Saying which counter, in words, next to the picker is the whole
                defence against reading a ward's stock as the front desk's. */}
            <div className="mb-6 flex flex-col sm:flex-row sm:items-center gap-2 px-3 py-2 rounded-xl bg-slate-50 border border-slate-200">
                <div className="flex items-center gap-2 text-xs text-slate-500">
                    <StoreIcon className="w-4 h-4 text-[#4ABDBD]" />
                    {t('medicinepage.stock_shown_for', 'Stock and bins shown for')}
                </div>
                <select
                    data-testid="medicines-store-picker"
                    value={storeId}
                    onChange={(e) => setStoreId(e.target.value)}
                    className="tt-select p-1.5 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                >
                    {stores.length === 0 && <option value="">{t('medicinepage.no_stores', 'No counter set up yet')}</option>}
                    {stores.map((s) => (
                        <option key={s.storeId} value={s.storeId}>
                            {s.name}{s.isDefault ? ` · ${t('medicinepage.default_store', 'default')}` : ''}
                        </option>
                    ))}
                </select>
                <span className="text-[11px] text-slate-400 sm:ml-auto">
                    {t('medicinepage.catalogue_is_shared', 'The catalogue itself is shared by every counter.')}
                </span>
            </div>

            {stockTruncated && (
                <div data-testid="medicines-stock-truncated" className="mb-4 px-3 py-2 rounded-xl bg-amber-50 border border-amber-200 text-xs text-amber-800">
                    {t('medicinepage.stock_truncated', 'This counter holds more batches than one screen can total. Quantities below cover the earliest-expiring {{n}} and may understate a few rows — use the stock report for exact balances.', { n: SWEEP_LIMIT * MAX_SWEEP_PAGES })}
                </div>
            )}

            {listLoading ? (
                <div className="flex flex-col items-center justify-center py-4 pt-12">
                    <CommonLoader />
                    <span className="text-xl text-[#4ABDBD]">{t('medicinepage.loading', 'Loading...')}</span>
                </div>
            ) : (
                <>
                    <div className="hidden lg:block overflow-x-auto rounded-md border border-gray-200">
                        <table data-testid="medicines-table" className="w-full border-collapse rounded-md overflow-hidden">
                            <thead>
                                <tr className="bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white">
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('medicinepage.serial_no', 'S.No')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('medicinepage.medicine', 'Medicine')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('medicinepage.brand', 'Brand')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('medicinepage.form_strength', 'Form / strength')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('medicinepage.schedule', 'Schedule')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('medicinepage.hsn_gst', 'HSN / GST')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('medicinepage.home_bin', 'Home bin')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('medicinepage.on_hand', 'On hand')}</th>
                                    <th className="p-3 text-center text-xs font-bold uppercase tracking-wider">{t('medicinepage.action', 'Action')}</th>
                                </tr>
                            </thead>
                            <tbody data-testid="medicines-table-body">
                                {medicines.length > 0 ? medicines.map((m, index) => (
                                    <tr
                                        key={m.medicineId}
                                        data-testid={`medicine-row-${m.medicineId}`}
                                        className="border-b border-gray-100/50 hover:bg-gradient-to-r hover:from-blue-50/80 hover:to-cyan-50/80 transition-all duration-300"
                                    >
                                        <td className="p-3 text-xs text-gray-600">{indexOfFirst + index + 1}</td>
                                        <td className="p-3 text-xs">
                                            <div className="flex items-center gap-2">
                                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${m.type === 'product' ? 'bg-amber-50' : 'bg-[#4ABDBD]/10'}`}>
                                                    {m.type === 'product'
                                                        ? <Package className="w-4 h-4 text-amber-600" />
                                                        : <Pill className="w-4 h-4 text-[#2D8A8A]" />}
                                                </div>
                                                <div>
                                                    <div className="font-semibold text-gray-700">{m.name || '-'}</div>
                                                    {m.type === 'product' ? (
                                                        <TypeBadge />
                                                    ) : (
                                                        /* The salt, not the trade name. It is what a
                                                           substitution is judged against. */
                                                        <div className="text-[11px] text-slate-400 italic">{m.genericName || t('medicinepage.no_generic', 'generic not recorded')}</div>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="p-3 text-xs text-gray-600">
                                            <div>{m.brand || '-'}</div>
                                            <div className="text-[11px] text-slate-400">{m.manufacturer || ''}</div>
                                        </td>
                                        <td className="p-3 text-xs text-gray-600">
                                            {/* A product has no dosage form — 'other' is only how it is stored. */}
                                            {m.type === 'product' ? (
                                                <span className="text-slate-300">—</span>
                                            ) : (
                                                <span className="inline-flex px-2 py-0.5 rounded bg-slate-100 text-slate-600 text-[11px] font-semibold capitalize">
                                                    {t(`medicinepage.form_${m.form}`, m.form || 'tablet')}
                                                </span>
                                            )}
                                            <div className="text-[11px] text-slate-400 mt-1">
                                                {[m.strength, m.packSize].filter(Boolean).join(' · ') || '—'}
                                            </div>
                                        </td>
                                        <td className="p-3 text-xs"><ScheduleBadge schedule={m.schedule} /></td>
                                        <td className="p-3 text-xs text-gray-600">
                                            <div className="font-mono">{m.hsnCode || '—'}</div>
                                            <div className="text-[11px] text-slate-400">
                                                {t('medicinepage.gst_at', 'GST {{rate}}%', { rate: Number(m.gstRate) || 0 })}
                                            </div>
                                        </td>
                                        <td className="p-3 text-xs"><BinBadge locationId={m.locationId} /></td>
                                        <td className="p-3 text-xs"><StockCell medicine={m} /></td>
                                        <td className="p-3 text-xs"><Actions medicine={m} /></td>
                                    </tr>
                                )) : (
                                    <tr>
                                        <td colSpan={9} className="p-6 text-center text-gray-500">
                                            {activeTab === 'active'
                                                ? t('medicinepage.no_items', 'Nothing here yet. Add the medicines and products this pharmacy sells — the counter dispenses from this list.')
                                                : t('medicinepage.no_discontinued', 'Nothing has been discontinued.')}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Cards below lg — nine columns do not survive a narrow screen,
                        and the pharmacy is one of the places a tablet is used. */}
                    <div className="block lg:hidden space-y-4">
                        {medicines.length > 0 ? medicines.map((m) => (
                            <div key={m.medicineId} data-testid={`medicine-card-${m.medicineId}`} className="p-4 bg-gray-50 rounded-xl shadow-md">
                                <div className="flex justify-between items-start gap-2 mb-2">
                                    <div>
                                        <div className="font-semibold text-gray-800 text-sm">{m.name || '-'}</div>
                                        {m.type !== 'product' && (
                                            <div className="text-[11px] text-slate-400 italic">{m.genericName || ''}</div>
                                        )}
                                        <div className="text-xs text-gray-600 mt-1">
                                            {(m.type === 'product'
                                                ? [m.brand, m.packSize]
                                                : [m.brand, m.strength, t(`medicinepage.form_${m.form}`, m.form)]
                                            ).filter(Boolean).join(' · ')}
                                        </div>
                                    </div>
                                    <Actions medicine={m} compact />
                                </div>
                                <div className="flex flex-wrap items-center gap-2 mt-3">
                                    {m.type === 'product' && <TypeBadge />}
                                    <BinBadge locationId={m.locationId} />
                                    <ScheduleBadge schedule={m.schedule} />
                                    <span className="px-2 py-0.5 rounded bg-white border border-gray-200 text-[11px] text-gray-600">
                                        {t('medicinepage.gst_at', 'GST {{rate}}%', { rate: Number(m.gstRate) || 0 })}
                                    </span>
                                    {m.hsnCode && (
                                        <span className="px-2 py-0.5 rounded bg-white border border-gray-200 text-[11px] font-mono text-gray-600">
                                            {m.hsnCode}
                                        </span>
                                    )}
                                </div>
                                <div className="mt-3"><StockCell medicine={m} /></div>
                            </div>
                        )) : (
                            <div className="p-6 text-center text-gray-500 text-sm">
                                {activeTab === 'active'
                                    ? t('medicinepage.no_items', 'Nothing here yet. Add the medicines and products this pharmacy sells — the counter dispenses from this list.')
                                    : t('medicinepage.no_discontinued', 'Nothing has been discontinued.')}
                            </div>
                        )}
                    </div>
                </>
            )}

            {!listLoading && total > 0 && (
                <Pagination
                    totalItems={total}
                    currentPage={currentPage}
                    itemsPerPage={itemsPerPage}
                    onPageChange={handlePageChange}
                />
            )}

            <AddMedicine
                isOpen={isModalOpen}
                editing={editing}
                locations={locations}
                storeName={selectedStore?.name || ''}
                onClose={() => { setIsModalOpen(false); setEditing(null); }}
                onSaved={refetch}
            />
        </div>
    );
}
